import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { announceSignOut } from "../../../../shared/hooks/useSignOutNotice";
import { signOutDeliberately } from "./deliberateSignOut";

/** How long Sign out waits for a hold's release before signing out anyway. */
export const SIGN_OUT_RELEASE_WAIT_MS = 2_000;

/**
 * The footer's Sign out, for one screen (components/SessionRow.tsx).
 *
 * Where it lands keeps the professional's rule: a professional who requires
 * a client account gets their sign-in gate, never a guest's service step;
 * anyone else gets the service step as a guest. "You're signed out." is said
 * only once Firebase has signed them out (auth/deliberateSignOut.ts).
 *
 * `releaseHold`, when given, is an UNFINISHED booking's temporary hold, let
 * go under the current uid first, and bounded so a dead network cannot keep
 * the person signed in. The caller passes none where nothing is held, and
 * never for a completed booking: signing out never cancels or releases a
 * confirmed appointment or a submitted request.
 *
 * A second press while one is under way does nothing.
 */
export interface BookSignOut {
  /** The footer's Sign out. */
  signOut(): void;
  /**
   * Where a sign-out Firebase has accepted (or may yet accept) is taking the
   * person, until they arrive: Book routes there ahead of its step guards, so
   * the previous person's Review or Details cannot send the next one onward
   * to Details or Time on the way out (QE-SO-1). Null otherwise.
   */
  leavingTo: string | null;
  /** They are there: Book's step guards apply again. */
  arrived(): void;
}

export function useBookSignOut({
  slug,
  requiresAccount,
  releaseHold,
}: {
  slug: string;
  requiresAccount: boolean;
  releaseHold?: () => Promise<void>;
}): BookSignOut {
  const navigate = useNavigate();
  const underWay = useRef(false);
  const [leavingTo, setLeavingTo] = useState<string | null>(null);
  const signOut = () => {
    if (underWay.current) return;
    underWay.current = true;
    const target = requiresAccount ? `/${slug}/book/signin` : `/${slug}/book/service`;
    // Applies only once the identity is no longer an account (Book), so
    // setting it while still signed in changes nothing on screen.
    setLeavingTo(target);
    const released = releaseHold
      ? Promise.race([
          releaseHold().catch(() => {}),
          new Promise<void>((resolve) => setTimeout(resolve, SIGN_OUT_RELEASE_WAIT_MS)),
        ])
      : Promise.resolve();
    void released
      .then(() =>
        signOutDeliberately({
          target,
          onSignedOut: () => {
            announceSignOut("signed-out");
            navigate(target, { replace: true });
          },
        })
      )
      .then(
        (outcome) => {
          // Staying signed in: nowhere to go.
          if (outcome === "stayed-signed-in") setLeavingTo(null);
        },
        () => {
          // Nothing in signOutDeliberately rejects today; if it ever does,
          // the person has not left, and Book's guards must apply again.
          setLeavingTo(null);
        }
      )
      .finally(() => {
        underWay.current = false;
      });
  };
  return { signOut, leavingTo, arrived: () => setLeavingTo(null) };
}
